import { Button } from '@mui/base/Button';
import { Input } from '@mui/base/Input';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import cx from 'classnames';
import { type ChangeEvent } from 'react';
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseControllerProps,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import typography from '~styles/typography.scss';
import autocompleteStyles from './ControlledFormInputAutocomplete.scss';
import styles from './ControlledFormInput.scss';
import { FormTooltip } from './FormTooltip';
import { LayoutVariant } from './sharedTypes';
import { ValidationErrorMessage } from './ValidationErrorMessage';

type AutocompleteInputSlotProps = React.ComponentPropsWithRef<'input'>;
type MuiBaseButtonProps = React.ComponentProps<typeof Button>;

export interface ControlledFormInputAutocompleteProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  className?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Name>;
  disabled?: boolean;
  label: string;
  name: Name;
  placeholder?: string;
  rules?: UseControllerProps<T, Name>['rules'];
  tooltipText?: string;
  type: string;
  variant?: LayoutVariant;
  autoComplete?: 'on' | 'off';
  ignoreOnePass?: boolean;
  readOnly?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: () => void;
  labelClassName?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  leadingOutsideInput?: React.ReactNode;
  darkMode?: boolean;
  /** MUI `useAutocomplete` root (`getRootProps` minus `ref`) + layout `className`. */
  inputRootSlotProps: React.HTMLAttributes<HTMLDivElement>;
  /** Forked ref: forwarded ref, `setAnchorEl`, and autocomplete root ref. */
  inputRootRef: React.Ref<HTMLDivElement> | null;
  /** MUI `useAutocomplete` `getInputProps()` (+ optional `placeholder`). */
  inputSlotProps: AutocompleteInputSlotProps;
  /**
   * From `const { onClick: muiClearClick, ...clearButtonProps } = getClearProps()`.
   * Renders the default "Clear" control inside the input end adornment.
   */
  autocompleteClear?: {
    clearButtonProps: Omit<MuiBaseButtonProps, 'onClick' | 'children'>;
    muiClearClick?: MuiBaseButtonProps['onClick'];
    onClear: () => void;
  };
}

export const ControlledFormInputAutocomplete = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  className,
  control,
  defaultValue,
  disabled,
  label,
  name,
  placeholder,
  rules,
  tooltipText,
  type,
  variant = LayoutVariant.Vertical,
  autoComplete = 'off',
  ignoreOnePass = true,
  readOnly = false,
  onBlur,
  onChange,
  onFocus,
  labelClassName = '',
  endAdornment = null,
  startAdornment = null,
  leadingOutsideInput = null,
  darkMode = false,
  inputRootRef,
  inputRootSlotProps,
  inputSlotProps,
  autocompleteClear,
}: ControlledFormInputAutocompleteProps<T, Name>) => {
  const {
    field: { ref },
    fieldState: { error },
  } = useController({
    control,
    defaultValue,
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
      },
    },
  });

  const {
    ref: slotPropsInputRef,
    onChange: slotBackedOnChange,
    onBlur: slotBackedOnBlur,
    onFocus: slotBackedOnFocus,
    className: slotInputClassName,
    placeholder: slotPlaceholder,
    ...slotInputRest
  } = inputSlotProps;

  const mergedInputRef = useForkRef(ref, slotPropsInputRef ?? null);

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    slotBackedOnFocus?.(e);
    onFocus?.();
    if (e.target.type === 'date') {
      try {
        e.target.showPicker?.();
      } catch (onFocusError) {
        if (
          onFocusError instanceof DOMException &&
          onFocusError.name === 'NotAllowedError'
        ) {
          /* Ignore the NotAllowedError.
           *
           * Me and my bad, bad homies
           * Doin' bad, bad things
           * But it feels so good
           *
           * Source: pH-1, “Mr. Bad” (feat. Woo), 2022
           * */
        } else {
          throw onFocusError;
        }
      }
    }
  };

  const labelFor = inputSlotProps.id ?? name;

  const hasEndAdornment = Boolean(endAdornment || autocompleteClear);

  const muiInput = (
    <Input
      aria-errormessage={`${name}-error`}
      aria-invalid={!!error}
      autoComplete={autoComplete}
      disabled={disabled}
      readOnly={readOnly}
      endAdornment={
        hasEndAdornment ? (
          <span className={styles.endAdornmentWrap}>
            {endAdornment}
            {autocompleteClear ? (
              <Button
                {...autocompleteClear.clearButtonProps}
                onClick={(e) => {
                  autocompleteClear.muiClearClick?.(e);
                  autocompleteClear.onClear();
                }}
              >
                Clear
              </Button>
            ) : null}
          </span>
        ) : null
      }
      startAdornment={startAdornment}
      onWheel={() => (document.activeElement as HTMLInputElement).blur()}
      slotProps={{
        root: {
          ...inputRootSlotProps,
          className: cx(
            styles.inputRoot,
            autocompleteStyles.autocompleteInputRoot,
            hasEndAdornment && styles.inputRootWithEndAdornment,
            inputRootSlotProps.className,
          ),
          ...(inputRootRef ? { ref: inputRootRef } : {}),
        },
        input: {
          ...slotInputRest,
          ref: mergedInputRef,
          placeholder: slotPlaceholder ?? placeholder,
          'aria-errormessage': `${name}-error`,
          'aria-invalid': !!error,
          // @ts-expect-error MUI base is not ready for 1pass attributes
          'data-1p-ignore': ignoreOnePass,
          'data-lpignore': ignoreOnePass,
          className: cx(
            {
              [typography.t1]: true,
              [styles.inputError]: !!error,
            },
            slotInputClassName,
          ),
          onChange: (e) => {
            slotBackedOnChange?.(e);
            onChange?.(e);
          },
          onBlur: (e) => {
            slotBackedOnBlur?.(e);
            onBlur?.(e);
          },
          onFocus: handleInputFocus,
        },
      }}
      type={type === 'email' ? 'text' : type}
    />
  );

  return (
    <div
      className={cx(
        {
          [styles.fieldContainer]: true,
          [styles[variant]]: true,
          [styles.darkMode]: darkMode,
        },
        className,
      )}
    >
      <div className={styles.labelContainer}>
        <label
          className={cx({
            [styles.labelError]: !!error,
            [labelClassName]: !!labelClassName,
          })}
          htmlFor={labelFor}
        >
          {label}
        </label>
        <FormTooltip label={name} text={tooltipText} />
      </div>
      <div className={styles.inputContainer}>
        <div className={styles.inputShell}>
          {muiInput}
          {leadingOutsideInput ? (
            <div className={styles.leadingOutsideInputSlot}>
              {leadingOutsideInput}
            </div>
          ) : null}
        </div>
        <ValidationErrorMessage error={error} label={label} name={name} />
      </div>
    </div>
  );
};
